/* Predictive search using Shopify's /search/suggest endpoint + Section Rendering. */
(function () {
  'use strict';

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this._bound) {
        // Re-attached (e.g. moved in the DOM): restore the listener removed in disconnectedCallback.
        document.addEventListener('click', this.onDocumentClick);
        return;
      }
      this._bound = true;
      this.enabled = this.dataset.predictiveEnabled === 'true';
      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.reset = this.querySelector('[data-search-reset]');
      this.panel = this.querySelector('[data-predictive-panel]');
      this.results = this.querySelector('[data-predictive-results]');
      this.loading = this.querySelector('[data-predictive-loading]');
      this.status = this.querySelector('[data-predictive-status]');
      this.cache = new Map();
      this.selectedIndex = -1;

      this.input.addEventListener('input', window.theme.debounce(this.onInput.bind(this), 220));
      this.input.addEventListener('input', () => this.toggleReset());
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results && this.results.innerHTML.trim()) this.open();
      });
      this.input.addEventListener('keydown', this.onKeydown.bind(this));
      this.form.addEventListener('submit', (event) => {
        if (!this.input.value.trim()) {
          event.preventDefault();
          this.input.focus();
        }
      });
      if (this.reset) {
        this.reset.addEventListener('click', (event) => {
          event.preventDefault();
          this.input.value = '';
          this.toggleReset();
          this.close();
          this.input.focus();
        });
      }
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onDocumentClick);
      // Close when keyboard focus leaves the search (Tab); clicks elsewhere are handled above.
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      this.toggleReset();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      if (this.controller) this.controller.abort();
    }

    toggleReset() {
      if (this.reset) this.reset.hidden = this.input.value.length === 0;
    }

    onInput() {
      if (!this.enabled || !this.panel) return;
      const term = this.input.value.trim();
      if (!term) {
        this.close();
        return;
      }
      this.search(term);
    }

    async search(term) {
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.setLoading(true);
      this.open();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,collection,query,page,article',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
        'resources[options][prefix]': 'last',
        // Match on title, model code (SKU), type and brand only, so suggestions stay close to the full results page.
        'resources[options][fields]': 'title,variants.sku,product_type,vendor',
        'resources[options][unavailable_products]': window.theme.hideSoldOut ? 'hide' : 'last',
        section_id: 'predictive-search'
      });

      try {
        const response = await fetch(`${window.theme.routes.predictiveSearch}?${params.toString()}`, {
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error(String(response.status));
        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        const html = section ? section.innerHTML : '';
        this.cache.set(term, html);
        if (this.input.value.trim() === term) this.render(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.innerHTML = `<p class="predictive-search__empty">${window.theme.strings.searchError}</p>`;
        this.open();
      } finally {
        this.setLoading(false);
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.selectedIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.open();
      const content = this.results.querySelector('[data-predictive-count]');
      if (this.status && content) {
        this.status.textContent = `${content.getAttribute('data-predictive-count')} ${window.theme.strings.searchResults}`;
      }
    }

    setLoading(isLoading) {
      if (this.loading) this.loading.hidden = !isLoading;
      this.panel.classList.toggle('is-loading', isLoading);
    }

    open() {
      if (!this.panel) return;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
      this.selectedIndex = -1;
    }

    options() {
      return Array.from(this.results.querySelectorAll('[role="option"]'));
    }

    onKeydown(event) {
      if (!this.panel || this.panel.hidden) {
        if (event.key === 'Escape' && this.input.value) {
          this.input.value = '';
          this.toggleReset();
        }
        return;
      }
      const options = this.options();
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.select(options, Math.min(this.selectedIndex + 1, options.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.select(options, Math.max(this.selectedIndex - 1, -1));
          break;
        case 'Enter': {
          const current = options[this.selectedIndex];
          const link = current && current.querySelector('a');
          if (link) {
            event.preventDefault();
            window.location.href = link.href;
          }
          break;
        }
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        default:
          break;
      }
    }

    select(options, index) {
      options.forEach((option) => option.setAttribute('aria-selected', 'false'));
      this.selectedIndex = index;
      if (index < 0) {
        this.input.removeAttribute('aria-activedescendant');
        return;
      }
      const option = options[index];
      option.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    }
  }

  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
})();
